import { Cushion, Typography, Grid, MaxWidth, Space, Rectangle, Hide } from '@pitchero/react-ui';
import React from 'react';
import PropTypes from 'prop-types';
import { Trans } from '../../lib/i18n';
import LotteryRules from './lottery-rules';
import ImportantFacts from './important-facts';
import SeoHead from '../seo';

const LotteryPage = ({ club, clubName, beEthicalCode, causeInformation }) => (
  <>
    <SeoHead
      club={club}
      titleTranslationKey="common:the_fundraising_club_lottery"
      descriptionTranslationKey="common:the_fundraising_club_lottery"
      route="lottery"
    />
    <Grid style={{ width: '100%' }} justifyContent="center">
      <Cushion>
        <picture>
          <source
            media="(max-width: 700px)"
            srcSet="https://images.pitchero.com/ccp-fundraising-club/mobile/win.png"
          />
          <img
            src="https://images.pitchero.com/ccp-fundraising-club/win.png"
            alt="The Fundraising Club"
          />
        </picture>
      </Cushion>

      <Cushion
        vertical="xlarge"
        horizontal="xlarge"
        style={{ backgroundColor: '#fff' }}
        component="div"
      >
        <MaxWidth component="div" center maxWidth={670}>
          <Cushion>
            <Typography
              component="h1"
              preset="title--large"
              transform="none"
              style={{ marginTop: 10, marginBottom: 10 }}
            >
              <Trans i18nKey="fundraisingClub:title" />
            </Typography>

            <Hide maxWidth="tab">
              <Space
                top="medium"
                bottom="large"
                responsive={[{ maxWidth: 'tab', props: { bottom: 'medium' } }]}
              >
                <Rectangle
                  fill="primary"
                  style={{ height: 4, maxWidth: 36, display: 'block', marginInlineStart: 0 }}
                  component="hr"
                />
              </Space>
            </Hide>

            <Typography
              component="div"
              preset="body--large"
              color="black"
              style={{ paddingBottom: 15 }}
            >
              <Trans i18nKey="fundraisingClub:section_1_line_1" values={{ clubName }} />
            </Typography>

            <Typography
              component="div"
              preset="body--large"
              color="black"
              style={{ paddingBottom: 15 }}
            >
              <strong>
                <Trans i18nKey="fundraisingClub:section_1_line_2" />
              </strong>
            </Typography>

            <Typography
              component="div"
              preset="body--large"
              color="black"
              style={{ paddingBottom: 15 }}
            >
              <Trans i18nKey="fundraisingClub:section_1_line_3" />
            </Typography>

            <Typography
              component="div"
              preset="body--large"
              color="black"
              style={{ paddingBottom: 15, fontWeight: 'bold' }}
            >
              <Trans i18nKey="fundraisingClub:section_1_line_4" />
            </Typography>

            <Cushion>
              <script src="https://joinourlottery.org.uk/widget/index.js" type="module" />
              <div
                id="belms-widget"
                data-url="https://joinourlottery.org.uk"
                data-subdomain="thefundraisinglottery"
                data-tr={beEthicalCode}
              />
            </Cushion>

            <Cushion>
              <picture>
                <source
                  media="(max-width: 700px)"
                  srcSet="https://images.pitchero.com/ccp-fundraising-club/mobile/Fundraising-Grants.png"
                />
                <img
                  style={{ paddingTop: 10 }}
                  src="https://images.pitchero.com/ccp-fundraising-club/Fundraising-Grants.png"
                  alt="Prizes"
                />
              </picture>
            </Cushion>

            <Cushion style={{ marginTop: 10, marginBottom: 10 }}>
              <picture>
                <source
                  media="(max-width: 700px)"
                  srcSet="https://images.pitchero.com/ccp-fundraising-club/mobile/GS.png"
                />
                <img
                  style={{ paddingTop: 10 }}
                  src="https://images.pitchero.com/ccp-fundraising-club/GS.png"
                  alt="Gourmet Society"
                />
              </picture>
            </Cushion>
          </Cushion>

          <Cushion>
            <Typography
              component="div"
              preset="body--large"
              color="black"
              style={{
                paddingTop: 15,
                paddingBottom: 15,
                color: '#0B375D',
                fontSize: 24,
                fontWeight: 'bold',
              }}
            >
              <Trans i18nKey="fundraisingClub:how_to_play_heading" />
            </Typography>
          </Cushion>

          <Cushion>
            <Typography
              component="div"
              preset="body--large"
              color="black"
              style={{ paddingBottom: 15, wordBreak: 'auto-phrase' }}
            >
              <Trans i18nKey="fundraisingClub:how_to_play_text_1" />
            </Typography>
          </Cushion>

          <Cushion>
            <Typography
              component="div"
              preset="body--large"
              color="black"
              style={{ paddingBottom: 15, wordBreak: 'auto-phrase' }}
            >
              <Trans i18nKey="fundraisingClub:how_to_play_text_2" />
            </Typography>
          </Cushion>

          <Cushion>
            <Typography
              component="div"
              preset="body--large"
              color="black"
              style={{ paddingBottom: 15, wordBreak: 'auto-phrase' }}
            >
              <Trans i18nKey="fundraisingClub:how_to_play_text_3" />
            </Typography>
          </Cushion>

          <Cushion>
            <Typography
              component="div"
              preset="body--large"
              color="black"
              style={{
                paddingTop: 15,
                paddingBottom: 15,
                color: '#0B375D',
                fontSize: 24,
                fontWeight: 'bold',
                wordBreak: 'auto-phrase',
              }}
            >
              <Trans i18nKey="fundraisingClub:how_does_the_club_benefit" />
            </Typography>
          </Cushion>

          <Cushion>
            <Typography
              component="div"
              preset="body--large"
              color="black"
              style={{ whiteSpace: 'pre-line', marginBottom: 20 }}
            >
              {causeInformation}
            </Typography>
          </Cushion>

          <LotteryRules />
          <ImportantFacts />

          <Cushion>
            <a
              href="https://pitche.ro/9pji6u"
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'inline-block', color: '#3091DB' }}
            >
              <picture>
                <source
                  media="(max-width: 700px)"
                  srcSet="https://images.pitchero.com/ccp-fundraising-club/mobile/Referral.png"
                />
                <img
                  style={{ paddingTop: 10 }}
                  src="https://images.pitchero.com/ccp-fundraising-club/Referral.png"
                  alt="Join The Fundraising Lottery"
                />
              </picture>
            </a>
          </Cushion>

          <Cushion>
            <a
              href="https://thefundraisinglottery.joinourlottery.org.uk/winners"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="View Results"
            >
              <picture>
                <source
                  media="(max-width: 700px)"
                  srcSet="https://images.pitchero.com/ccp-fundraising-club/mobile/Results.png"
                />
                <img
                  style={{ paddingTop: 10 }}
                  src="https://images.pitchero.com/ccp-fundraising-club/Results.png"
                  alt="JResults"
                />
              </picture>
            </a>
          </Cushion>

          <Cushion component="div" style={{ paddingBottom: 15, paddingTop: 20 }}>
            <Typography component="div" style={{ paddingBottom: 15 }}>
              <Trans i18nKey="fundraisingClub:footer_p_1" />
            </Typography>
            <Typography component="div" style={{ paddingBottom: 15 }}>
              <Trans i18nKey="fundraisingClub:footer_p_2" />
            </Typography>
            <Typography component="div" style={{ paddingBottom: 15 }}>
              <Trans
                i18nKey="fundraisingClub:footer_p_3"
                components={{
                  a: (
                    <a
                      href="https://www.gamblingcommission.gov.uk/public-register/business/detail/44067"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'inline-block', color: '#3091DB' }}
                    >
                      44067
                    </a>
                  ),
                }}
              />
            </Typography>
            <Typography
              component="div"
              style={{ whiteSpace: 'pre-line', marginTop: 20, marginBottom: 20 }}
            >
              <Trans i18nKey="fundraisingClub:footer_p_4" />
            </Typography>
          </Cushion>

          <Cushion>
            <picture>
              <source
                media="(max-width: 700px)"
                srcSet="https://images.pitchero.com/ccp-fundraising-club/mobile/18.png"
              />
              <img
                style={{ paddingTop: 10 }}
                src="https://images.pitchero.com/ccp-fundraising-club/18.png"
                alt="18 + Be Gamble Aware"
              />
            </picture>
          </Cushion>
        </MaxWidth>
      </Cushion>
    </Grid>
  </>
);

LotteryPage.propTypes = {
  club: PropTypes.shape.isRequired,
  clubName: PropTypes.string.isRequired,
  beEthicalCode: PropTypes.string.isRequired,
  causeInformation: PropTypes.string.isRequired,
};

export default LotteryPage;
